'use client';

import * as React from 'react';
import { type IconName } from '@/hen-ui/atomics/Icon';
import { Accordion } from '@/hen-ui/composites/Accordion';
import { Flex } from '@/hen-ui/composites/Flex';
import { Tabs } from '@/hen-ui/composites/Tabs';

// ─── Types ────────────────────────────────────────────────────────────────────

export type CollapsibleInfoPanelSectionKey =
  | 'dateFields'
  | 'dimensions'
  | 'baseMeasures'
  | 'calculatedMetrics';

export interface CollapsibleInfoPanelSection {
  /** Display label shown on the pill toggle for this section. */
  label: string;
  /** Content to render inside this section panel. */
  content: React.ReactNode;
}

export interface CollapsibleInfoPanelProps {
  /**
   * Label shown in the collapsed trigger bar.
   * @example "Data Source Context"
   */
  title: string;
  /**
   * Content for each of the 4 documentation sections.
   * Each section is individually configurable per dashboard tab.
   */
  sections: Record<CollapsibleInfoPanelSectionKey, CollapsibleInfoPanelSection>;
  /**
   * Which pill tab is active on initial render.
   * @default the first key in `sections`
   */
  defaultSection?: CollapsibleInfoPanelSectionKey;
  /**
   * Lucide icon shown in the trigger bar alongside the title.
   * @default 'database'
   */
  icon?: IconName;
  /**
   * Whether the panel starts expanded.
   * @default false
   */
  defaultOpen?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * CollapsibleInfoPanel
 *
 * A generic collapsible panel with a trigger bar and 4 pill-toggled content
 * sections. Designed for use at the bottom of a content area — e.g. a partner
 * dashboard tab — where contextual reference information is useful but should
 * not dominate the view.
 *
 * - Default state: collapsed (single-line bar)
 * - Expanded state: panel with 4 pill-toggled sections
 * - Title and all section content are fully prop-driven (pure UI, no data fetching)
 *
 * @example
 * <CollapsibleInfoPanel
 *   title="Data Source Context"
 *   sections={{
 *     dateFields: { label: 'Date Fields', content: <Text variant="b5">Fall term = Aug 1 – Dec 31</Text> },
 *     dimensions: { label: 'Dimensions', content: <Text variant="b5">Program, campus, residency</Text> },
 *     baseMeasures: { label: 'Base Measures', content: <Text variant="b5">Submitted, Accepted, Enrolled</Text> },
 *     calculatedMetrics: { label: 'Calculated Metrics', content: <Text variant="b5">YoY % = (current – prior) / prior</Text> },
 *   }}
 * />
 */
export const CollapsibleInfoPanel: React.FC<CollapsibleInfoPanelProps> = ({
  title,
  icon = 'database',
  sections,
  defaultSection = Object.keys(sections)[0] as CollapsibleInfoPanelSectionKey,
  defaultOpen = false,
}) => {
  const tabs = (Object.keys(sections) as CollapsibleInfoPanelSectionKey[]).map(
    (key) => ({
      value: key,
      label: sections[key].label,
      content: sections[key].content,
    }),
  );

  return (
    <Accordion
      type='single'
      collapsible
      defaultValue={defaultOpen ? 'panel' : undefined}
      variant='white'
      items={[
        {
          value: 'panel',
          title: title,
          icon: icon,
          iconBg: false,
          content: (
            <Flex direction='column' gap='md'>
              <Tabs
                tabs={tabs}
                defaultValue={defaultSection}
                className='w-full'
              />
            </Flex>
          ),
        },
      ]}
    />
  );
};

CollapsibleInfoPanel.displayName = 'CollapsibleInfoPanel';
